/**
 * Two-Factor Authentication Page Logic
 *
 * Handles 2FA code verification
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { getLoginRedirect } from '../app/init.js';
import { injectLayout } from '../app/layout.js';
import { applyTheme } from '../app/theme.js';
import { formSubmitHandler } from '../ui/forms.js';
import type { ApiClientError } from '../api/client.js';
import { twofaErrorAction } from './twofa-errors.js';

/**
 * Show a verify/resend error, or send the user back to sign in when the
 * challenge can't be completed any more. The login page shows the notice.
 */
function handleTwofaError(err: ApiClientError, errorDiv: HTMLElement | null): void {
  const action = twofaErrorAction(err.code, err.message);
  if (action.kind === 'sign-in-again') {
    sessionStorage.removeItem('pending_token');
    sessionStorage.removeItem('requires_totp');
    sessionStorage.setItem('login_notice', action.notice);
    window.location.href = '/login';
    return;
  }
  if (errorDiv) errorDiv.textContent = action.message;
}

/**
 * Handle 2FA code verification
 */
async function handleVerify(): Promise<void> {
  const codeInput = document.getElementById('code') as HTMLInputElement;
  const errorDiv = document.getElementById('twofa-error');
  if (errorDiv) errorDiv.textContent = '';

  const code = codeInput?.value || '';

  if (!code) {
    if (errorDiv) errorDiv.textContent = 'Please enter the verification code';
    return;
  }

  const trustCheckbox = document.getElementById('trust-device') as HTMLInputElement;
  const trustDevice = trustCheckbox?.checked ?? false;
  const result = await apiCall(api.verify2fa(code, trustDevice), undefined,
    (err) => handleTwofaError(err, errorDiv));
  if (!result) return;

  if (result.session_token) {
    // Redirect to intended page (device token storage handled by api client)
    window.location.href = getLoginRedirect();
  } else {
    if (errorDiv) errorDiv.textContent = 'Invalid verification code';
  }
}

/**
 * Initialize the 2FA page
 */
export async function init2faPage(): Promise<void> {
  applyTheme();
  // Inject minimal layout (no user info since not fully authenticated)
  injectLayout(null);

  // Check if this is a TOTP flow
  const isTotpMode = sessionStorage.getItem('requires_totp') === 'true';

  if (isTotpMode) {
    // Update UI for TOTP mode
    const heading = document.querySelector('h4');
    if (heading) {
      heading.textContent = 'Enter the verification code from your authenticator app.';
    }

    // Hide resend button and email help text (not applicable for TOTP)
    const resendBtn = document.getElementById('resend-btn');
    if (resendBtn) {
      resendBtn.style.display = 'none';
    }

    const helpText = document.querySelector('p[style*="font-size: 0.9em"]');
    if (helpText) {
      (helpText as HTMLElement).style.display = 'none';
    }
  }

  // Set up form handler with loading state
  const verifyForm = document.getElementById('twofa-form');
  if (verifyForm) {
    formSubmitHandler(verifyForm, handleVerify);
  }

  // Set up resend button (email mode only — already hidden for TOTP above)
  const resendBtn = document.getElementById('resend-btn');
  if (resendBtn && !isTotpMode) {
    resendBtn.addEventListener('click', async () => {
      const messageDiv = document.getElementById('twofa-message');
      const errorDiv = document.getElementById('twofa-error');
      if (errorDiv) errorDiv.textContent = '';

      const result = await apiCall(api.resendCode(), undefined,
        (err) => handleTwofaError(err, errorDiv));
      if (result && messageDiv) {
        messageDiv.innerHTML = '<p style="color: green;">A new verification code has been sent to your email.</p>';
      }
    });
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).init2faPage = init2faPage;
}
